import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, CalendarClock, Inbox } from "lucide-react";
import { toast } from "sonner";

const statusBadge = (s) => ({ new: "bg-nhs-blue/10 text-nhs-blue", contacted: "bg-nhs-amber/20 text-[#8a6400]", closed: "bg-slate-200 text-slate-600" }[s] || "bg-slate-100 text-slate-700");

export default function Enquiries() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");

    const load = () => api.get("/enquiries").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try { await api.patch(`/enquiries/${id}?status=${status}`); toast.success("Updated"); load(); }
        catch { toast.error("Update failed"); }
    };

    const shown = filter === "all" ? items : items.filter((i) => i.status === filter);

    return (
        <div className="space-y-6" data-testid="enquiries-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">Enquiries & Bookings</h1>
                    <p className="text-slate-500 mt-1">Coaching, speaking, training and consultancy requests from the public site.</p>
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-40 bg-white" data-testid="enquiry-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {["new", "contacted", "closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {shown.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400 flex flex-col items-center gap-3" data-testid="empty-enquiries">
                    <Inbox className="h-8 w-8" /> No enquiries yet.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-6">
                    {shown.map((e) => (
                        <div key={e.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid={`enquiry-${e.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="font-heading font-semibold text-lg text-nhs-ink">{e.name}</h3>
                                    <p className="text-sm text-slate-500">{e.service || "General enquiry"}</p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <Badge className={`border-0 ${e.request_type === "call_request" ? "bg-nhs-teal/15 text-nhs-teal" : "bg-slate-100 text-slate-600"}`}>
                                        {e.request_type === "call_request" ? "Call request" : "Enquiry"}
                                    </Badge>
                                    <Badge className={`border-0 capitalize ${statusBadge(e.status)}`}>{e.status}</Badge>
                                </div>
                            </div>
                            {e.message && <p className="mt-3 text-sm text-slate-700 leading-relaxed">{e.message}</p>}
                            <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                                <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {e.email}</p>
                                {e.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {e.phone}</p>}
                                {e.request_type === "call_request" && (e.preferred_date || e.preferred_time) && (
                                    <p className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-slate-400" /> {e.preferred_date} {e.preferred_time}</p>
                                )}
                            </div>
                            <div className="mt-4 flex items-center gap-2">
                                <span className="text-xs text-slate-400">Set status:</span>
                                <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                                    <SelectTrigger className="h-8 w-36" data-testid={`enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>{["new", "contacted", "closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
